import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ClockIcon } from '@heroicons/react/24/outline'
import LoadingSpinner from '@/components/LoadingSpinner'
import { useRedemptions } from '@/hooks/useRewards'
import { useDateFormatter } from '@/hooks/useDateFormatter'
import type { RedemptionHistoryItem } from '@/types'

interface RedemptionCardProps {
  item: RedemptionHistoryItem
}

function RedemptionCard({ item }: RedemptionCardProps) {
  const { t } = useTranslation('rewards')
  const { formatDate } = useDateFormatter()

  return (
    <div className="flat-card p-4">
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-base-content leading-snug">{item.reward_name}</p>
          <p className="text-xs text-base-content/50 mt-0.5">{formatDate(item.created_at)}</p>
          {item.fulfillment_ref && (
            <p className="text-xs font-mono text-base-content/40 mt-0.5">
              {t('fulfillment_ref', { ref: item.fulfillment_ref })}
            </p>
          )}
        </div>
        <span className="text-sm font-bold text-error shrink-0">
          {t('history_points', { points: item.points_spent.toLocaleString() })}
        </span>
      </div>
    </div>
  )
}

export default function RewardHistoryPage() {
  const { t } = useTranslation('rewards')
  const { t: tc } = useTranslation('common')
  const [page, setPage] = useState(1)
  const { data, isLoading, error } = useRedemptions(page)

  if (isLoading) return <LoadingSpinner />

  if (error) {
    return (
      <div className="p-4">
        <div className="alert alert-error">{tc('errors.something_went_wrong')}</div>
      </div>
    )
  }

  const items = data?.data ?? []
  const meta = data?.meta

  return (
    <div className="p-4 space-y-3">
      {/* Empty state */}
      {!isLoading && items.length === 0 && (
        <div className="text-center py-16 space-y-3">
          <div className="flex justify-center">
            <div className="icon-container">
              <ClockIcon className="w-5 h-5" />
            </div>
          </div>
          <p className="text-sm text-base-content/60">{t('no_history')}</p>
        </div>
      )}

      {/* List */}
      {items.map((item) => (
        <RedemptionCard key={item.id} item={item} />
      ))}

      {/* Pagination */}
      {meta && meta.total_pages > 1 && (
        <div className="flex gap-2 justify-center pt-2">
          <button
            className="btn btn-ghost btn-sm"
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
          >
            ‹
          </button>
          <span className="btn btn-ghost btn-sm no-animation cursor-default">
            {page} / {meta.total_pages}
          </span>
          <button
            className="btn btn-ghost btn-sm"
            disabled={page >= meta.total_pages}
            onClick={() => setPage((p) => p + 1)}
          >
            ›
          </button>
        </div>
      )}
    </div>
  )
}
